import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AlertService } from '@pharvio/core/ui';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { StepUpCancelledError } from './step-up-cancelled.error';
import { ClearStepUp, RequestStepUpChallenge } from './step-up.actions';
import { STEP_UP_VERIFY_EMAIL_ROUTE } from './step-up.config';
import { StepUpDialogComponent } from './step-up-dialog/step-up-dialog.component';
import { serialiseStepUpPayload, StepUpState } from './step-up.state';

@Injectable({ providedIn: 'root' })
export class StepUpService {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly alert = inject(AlertService);
  private readonly router = inject(Router);
  /** Null when the host app has no email-verification screen. */
  private readonly verifyEmailRoute = inject(STEP_UP_VERIFY_EMAIL_ROUTE, {
    optional: true
  });

  /**
   * Pre-flight step-up. Requests a challenge for `operation + payload`, opens
   * the OTP dialog, and resolves once the user verifies.
   *
   * The token is held in StepUpState and the interceptor attaches it only to
   * the request whose body serialises identically to `payload`. Don't mutate
   * `payload` between this call and the request — any change invalidates the
   * token (the interceptor then falls back to the reactive 403 flow).
   *
   * @throws {StepUpCancelledError} when the user dismisses the OTP dialog, or
   * when no challenge could be issued (the user has already been told why).
   */
  async ensure(operation: string, payload: unknown): Promise<void> {
    const token = await this.challenge(operation, payload);
    if (!token) {
      throw new StepUpCancelledError();
    }
  }

  /**
   * Request a challenge and, **only if one was issued**, collect the code.
   * A challenge that can't be issued never opens the OTP dialog — there'd be
   * nothing to type a code against. The reason is surfaced here instead: an
   * unverified email address gets a modal offering to fix it, anything else a
   * toast.
   *
   * @returns the step-up token, or `null` when the user cancelled / no
   * challenge could be issued.
   */
  async challenge(operation: string, payload: unknown): Promise<string | null> {
    this.store.dispatch(new ClearStepUp());
    await firstValueFrom(
      this.store.dispatch(new RequestStepUpChallenge(operation, payload)),
      { defaultValue: null }
    );

    if (!this.store.selectSnapshot(StepUpState.activeChallenge)) {
      await this.explainMissingChallenge();
      return null;
    }

    return this.openDialog();
  }

  /**
   * Opens the step-up OTP dialog (non-dismissible). Returns the token string
   * on success or `null` on cancel. Only call this with an active challenge.
   */
  openDialog(): Promise<string | null> {
    const ref = this.dialog.open<StepUpDialogComponent, void, string | null>(
      StepUpDialogComponent,
      {
        disableClose: true,
        panelClass: 'step-up-dialog'
      }
    );
    return firstValueFrom(ref.afterClosed().pipe(map((v) => v ?? null)));
  }

  /**
   * Take the held token if it was issued for exactly this request body.
   * Tokens are single-use, so a match clears it from state.
   */
  consumeTokenFor(body: unknown): string | null {
    const token = this.store.selectSnapshot(StepUpState.token);
    const payloadJson = this.store.selectSnapshot(StepUpState.payloadJson);
    if (!token || payloadJson !== serialiseStepUpPayload(body)) return null;

    this.store.dispatch(new ClearStepUp());
    return token;
  }

  /** Tell the user why no code is coming, and offer the way out when there is one. */
  private async explainMissingChallenge(): Promise<void> {
    const message =
      this.store.selectSnapshot(StepUpState.error) ??
      'We could not send a verification code. Please try again.';

    const needsEmailVerification = this.store.selectSnapshot(
      StepUpState.needsEmailVerification
    );

    if (!needsEmailVerification || !this.verifyEmailRoute) {
      this.alert.error(message);
      return;
    }

    const goVerify = await firstValueFrom(
      this.alert.showConfirmationModal<boolean>({
        action: 'warning',
        title: 'Verify your email first',
        message,
        primaryButton: { title: 'Verify my email', value: true },
        secondaryButton: { title: 'Not now', value: false }
      }),
      { defaultValue: false }
    );

    if (goVerify) {
      await this.router.navigateByUrl(this.verifyEmailRoute);
    }
  }
}
